// Reusable Schema.org JSON-LD builders for listings, providers, reviews, FAQs.
// Returns objects suitable for `JSON.stringify` into `<script type="application/ld+json">`.

export type Money = { value: number; currency?: string };
export type ReviewInput = { author: string; rating: number; body?: string; datePublished?: string };

export function orgRef() {
  return { "@type": "Organization", name: "Peopels", url: "/" } as const;
}

export function breadcrumb(items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.name, item: it.path,
    })),
  };
}

export function listingDetail(input: {
  id: string; name: string; description?: string; image?: string; category?: string;
  price?: Money; areaServed?: string; url: string; provider?: ProviderInput;
  aggregate?: { rating: number; count: number }; reviews?: ReviewInput[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": input.url + "#service",
    name: input.name,
    description: input.description,
    image: input.image,
    serviceType: input.category,
    areaServed: { "@type": "AdministrativeArea", name: input.areaServed ?? "Greater London" },
    offers: input.price ? {
      "@type": "Offer", price: input.price.value, priceCurrency: input.price.currency ?? "GBP", availability: "https://schema.org/InStock", url: input.url,
    } : undefined,
    provider: input.provider ? providerProfile(input.provider) : orgRef(),
    aggregateRating: input.aggregate ? {
      "@type": "AggregateRating", ratingValue: input.aggregate.rating, reviewCount: input.aggregate.count, bestRating: 5, worstRating: 1,
    } : undefined,
    review: input.reviews?.map(reviewSchema),
    url: input.url,
  };
}

export type ProviderInput = {
  id: string; name: string; image?: string; url: string; jobTitle?: string;
  areaServed?: string; aggregate?: { rating: number; count: number }; reviews?: ReviewInput[];
  memberSince?: string;
};

export function providerProfile(p: ProviderInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": p.url + "#person",
    name: p.name,
    image: p.image,
    jobTitle: p.jobTitle ?? "Helper",
    url: p.url,
    worksFor: orgRef(),
    areaServed: { "@type": "AdministrativeArea", name: p.areaServed ?? "Greater London" },
    aggregateRating: p.aggregate ? {
      "@type": "AggregateRating", ratingValue: p.aggregate.rating, reviewCount: p.aggregate.count, bestRating: 5, worstRating: 1,
    } : undefined,
    review: p.reviews?.map(reviewSchema),
  };
}

export function reviewSchema(r: ReviewInput) {
  return {
    "@type": "Review",
    author: { "@type": "Person", name: r.author },
    reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
    reviewBody: r.body,
    datePublished: r.datePublished,
  };
}

export function reviewPage(input: { url: string; itemName: string; reviews: ReviewInput[]; aggregate?: { rating: number; count: number } }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemPage",
    url: input.url,
    mainEntity: {
      "@type": "Product",
      name: input.itemName,
      aggregateRating: input.aggregate ? {
        "@type": "AggregateRating", ratingValue: input.aggregate.rating, reviewCount: input.aggregate.count, bestRating: 5, worstRating: 1,
      } : undefined,
      review: input.reviews.map(reviewSchema),
    },
  };
}

export function faqPage(qa: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qa.map((f) => ({
      "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
